import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getSystemLevelNotifications,
  updateSystemLevelNotification,
} from 'src/services/client';
import { onCheckUserCanAccessTheModule } from 'src/helpers/validationHelper';
import { USER_ROLES } from 'src/common/constants';
import {
  SystemLevelNotification,
  SystemLevelNotificationGroups,
  SystemLevelNotificationsResponse,
  NotificationChannel,
  UseSystemLevelNotificationsReturn,
} from './types';

const ALLOWED_ROLES_FOR_SYSTEM_NOTIFICATIONS = [
  USER_ROLES.ADMIN,
  USER_ROLES.SUPER_ADMIN,
];

const ALLOWED_ROLES_FOR_QR_CODE = [
  USER_ROLES.PROPERTY_DEVELOPER,
  USER_ROLES.ADMIN,
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SERVICE_MANAGER,
  USER_ROLES.STRATA_MANAGER,
];

export const useSystemLevelNotifications =
  (): UseSystemLevelNotificationsReturn => {
    const [notifications, setNotifications] = useState<
      SystemLevelNotification[]
    >([]);
    const [savedNotifications, setSavedNotifications] = useState<
      SystemLevelNotification[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canAccessSystemNotifications: any = onCheckUserCanAccessTheModule(
      ALLOWED_ROLES_FOR_SYSTEM_NOTIFICATIONS
    );

    const canAccessQRCode: any = onCheckUserCanAccessTheModule(
      ALLOWED_ROLES_FOR_QR_CODE
    );

    const fetchNotifications = useCallback(async () => {
      if (!canAccessSystemNotifications) {
        setIsLoading(false);
        return;
      }

      try {
        const response: any = await getSystemLevelNotifications();

        if (response.status === 200) {
          const jsonResponse: SystemLevelNotificationsResponse =
            await response.json();

          setNotifications(jsonResponse.data);
          setSavedNotifications(jsonResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch system level notifications:', error);
      } finally {
        setIsLoading(false);
      }
    }, [canAccessSystemNotifications]);

    useEffect(() => {
      fetchNotifications();
    }, [fetchNotifications]);

    const toggleChannel = (
      notificationId: number,
      channel: NotificationChannel
    ) => {
      const updatedNotifications: SystemLevelNotification[] = notifications.map(
        (notification: SystemLevelNotification) => {
          const isTargetNotification: boolean =
            notification.id === notificationId;

          if (!isTargetNotification) {
            return notification;
          }

          const currentChannelValue: boolean = notification.groups[channel];
          const toggledChannelValue: boolean = !currentChannelValue;

          const updatedGroups: SystemLevelNotificationGroups = {
            ...notification.groups,
            [channel]: toggledChannelValue,
          };

          return { ...notification, groups: updatedGroups };
        }
      );

      setNotifications(updatedNotifications);
    };

    const hasChanges: boolean =
      JSON.stringify(notifications) !== JSON.stringify(savedNotifications);

    const submitChanges = async () => {
      if (!hasChanges) {
        return;
      }

      setIsSubmitting(true);

      try {
        const notificationUpdates = notifications.map(
          (notification: SystemLevelNotification) => ({
            id: notification.id,
            groups: notification.groups,
          })
        );

        const response: any = await updateSystemLevelNotification(
          notificationUpdates
        );

        if (response.status === 200 || response.ok) {
          const jsonResponse: SystemLevelNotificationsResponse =
            await response.json();

          setNotifications(jsonResponse.data);
          setSavedNotifications(jsonResponse.data);

          toast.success('Notification settings updated.', {
            autoClose: 3000,
            position: 'top-center',
          });
        } else {
          toast.error("Couldn't update the notification settings", {
            autoClose: 12000,
            position: 'top-center',
          });
        }
      } catch (error) {
        console.error('Failed to update system notifications:', error);
        toast.error('Something went wrong.', {
          autoClose: 12000,
          position: 'top-center',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return {
      notifications,
      isLoading,
      isSubmitting,
      hasChanges,
      canAccessSystemNotifications,
      canAccessQRCode,
      toggleChannel,
      submitChanges,
      refetch: fetchNotifications,
    };
  };
